import {
  EVENT_AIDE_EXIT,
  EVENT_APPLICATION_SUBMITTED,
  EVENT_BASKET_OPEN_SCREEN_SAVE_MODAL,
  EVENT_BASKET_DIFF_OPENED,
  EVENT_BASKET_SAVED,
  EVENT_BASKET_UPDATED,
  EVENT_COLLECTIVE_LOADED,
  EVENT_CONTRACT_OWNER_CHANGED,
  EVENT_CUSTOMER_PORTAL_CHANGED,
  EVENT_FAB_OPENED,
  EVENT_FORM_INIT,
  EVENT_FORM_INTERACTED,
  EVENT_FORM_CANCELED,
  EVENT_FORM_ERROR,
  EVENT_FORM_SUBMITTED,
  EVENT_LANGUAGE_CHANGED,
  EVENT_NUDGE_CLOSE,
  EVENT_PERSONS_FORM_DISPLAYED,
  EVENT_PRODUCT_ADDED,
  EVENT_PRODUCT_DETAIL_OPEN,
  EVENT_PRODUCT_DURATION_CHANGED,
  EVENT_PRODUCT_FRANCHISE_CHANGED,
  EVENT_PRODUCT_REMOVED,
  EVENT_PRODUCT_STRUCTURE_SET,
  EVENT_REENTRY_LOADED,
  EVENT_SYNC_EXECUTED,
  EVENT_OVP_ENTERED,
  EVENT_TRANSFER_DOCTOR_FROM_OTHER_DISPLAYED,
  EVENT_TRANSFER_DOCTOR_FROM_OTHER_APPLIED,
  EVENT_TRACK_AFTER_BASKET_UPDATE,
  EVENT_COBROWSE_PIN_DIALOG_OPEN,
  EVENT_COBROWSE_SESSION_STARTED,
  EVENT_COBROWSE_SESSION_ENDED,
  EVENT_PARTNER_WIDGET_DISPLAYED,
  EVENT_PARTNER_WIDGET_PAGE_VIEWED,
  EVENT_PARTNER_WIDGET_GOTO_SANITAS,
} from '@/config/events'
import { RULES, ANSWER } from '@/config/tracker'
import { DistributionHelper } from '@/helpers/Distribution'
import { TrackerHelper } from '@/helpers/Tracker'

let bus, _basket, _productsStructure
let screenPersonsFunneledDelay = false

const queuedRulesAfterBasketUpdate = []

export default {
  install(Vue) {
    bus = Vue.prototype.$EventBus

    TrackerHelper.tracker.set({
      data: { _version: { fe: { ovp: Vue.prototype.$version } } },
    })

    bus.$on(EVENT_AIDE_EXIT, () => {
      TrackerHelper.trackAction(RULES.AIDE_EXIT)
    })

    // Language
    bus.$on(EVENT_LANGUAGE_CHANGED, ({ language }) => {
      TrackerHelper.tracker.set({ data: { page: { language } } })
      TrackerHelper.trackAction(RULES.OVP_LANGUAGE_CHANGED)
    })

    // save-nudge
    bus.$on(EVENT_NUDGE_CLOSE, () => {
      TrackerHelper.trackAction(RULES.OVP_SAVE_NUDGE_CLOSED)
    })

    // Persons form
    bus.$on(EVENT_PERSONS_FORM_DISPLAYED, ({ source, trackingData }) => {
      TrackerHelper.setTrackingSourceComponent(source)
      if (trackingData) {
        TrackerHelper.tracker.set({ data: { ...trackingData } })
      }

      // TODO Tracking: This seems weird, distributionPartners is always set. Check if correctly tracked
      if (_basket.collective && !_basket.distributionPartners) {
        screenPersonsFunneledDelay = true
      } else {
        TrackerHelper.trackAction(RULES.OVP_SCREEN_PERSONS_FUNNELED)
      }
    })

    // Form Tracking!
    window.setTimeout(() => {
      ;[EVENT_FORM_INIT, EVENT_FORM_INTERACTED, EVENT_FORM_CANCELED, EVENT_FORM_ERROR, EVENT_FORM_SUBMITTED].forEach(
        key =>
          bus.$on(key, event => {
            TrackerHelper.setFormTrackingData(key, event)
            TrackerHelper.trackAction(key, false, event)
          })
      )
    })

    bus.$on(EVENT_COLLECTIVE_LOADED, collectiveConfig => {
      const distributionPartners = DistributionHelper.createDistributionPartners(
        collectiveConfig.distributionPartnerSanitas
      )
      TrackerHelper.setDistributionPartners(distributionPartners)

      if (screenPersonsFunneledDelay) {
        TrackerHelper.trackAction(RULES.OVP_SCREEN_PERSONS_FUNNELED)
        screenPersonsFunneledDelay = false
      }
    })

    // Products update related
    bus.$on(EVENT_PRODUCT_STRUCTURE_SET, productsStructure => {
      _productsStructure = productsStructure

      if (!_basket?.persons) return

      TrackerHelper.tracker.set({
        data: {
          basket: {
            products: TrackerHelper.getProducts(_basket.persons, _productsStructure),
            productsCombination: TrackerHelper.computeProductsCombination(_basket.persons, _productsStructure),
          },
        },
      })
    })

    // Basket update related
    bus.$on(EVENT_BASKET_UPDATED, basket => {
      _basket = { ...basket }

      // if there's no basketId, then it's not a valid basket, yo!
      if (!basket.basketId) return

      const personsWithOffer = basket.persons?.filter(p => p.oaOfferId)

      const personsScreenSource = `${TrackerHelper.isCollective(basket) ? `${TrackerHelper.COLLECTIVE_PREFIX}-` : ''}${
        basket.channel
      }`
      const firstPerson = basket.persons[0]
      const preInsurer = firstPerson?.preInsurer
      const preInsurerAnswer =
        preInsurer?.preInsurer === false ? ANSWER.NO : firstPerson?.preInsurer?.currentPreInsurerId ?? ANSWER.NONE
      const hasMultiYearProducts = TrackerHelper.getBooleanAnswer(TrackerHelper.hasMultiYearProducts(basket.persons))

      const data = {
        data: {
          basket: {
            id: basket?.basketId,
            countBaskets: 1,
            countPersonsWithOffer: personsWithOffer.length,
            partnerWidgetBundleCode: basket?.partnerWidgetBundleCode,
            offerIds: personsWithOffer.map(p => p.oaOfferId).join(';'),
            masterTreaty: basket?.masterTreaty,
            productionValue: TrackerHelper.computeProductsValue(basket.persons),
            products: TrackerHelper.getProducts(basket.persons, _productsStructure),
            productsCombination: TrackerHelper.computeProductsCombination(basket.persons, _productsStructure),
            secured: TrackerHelper.getBooleanAnswer(!!basket.email && !!basket.mobile),
            immigrant: TrackerHelper.getBooleanAnswer(!!basket.immigrant),
            countDocuments: TrackerHelper.computeNumberOfUploads(basket.persons),
            preInsurerVvgs: TrackerHelper.computePreInsurerVVG(basket.persons, true).join(';'),
            hasMultiYearProducts,
            hasSignature: TrackerHelper.getBooleanAnswer(!!basket.signature?.signatureRequired),
            countPreInsurerVvgs: TrackerHelper.computePreInsurerVVG(basket.persons).length,
            existingCustomer: TrackerHelper.getBooleanAnswer(!!basket.existingCustomer),
          },
          collectiveContractNumber: basket.collective?.contractNumber,
          familySize: basket.persons.length,
          firstPerson: {
            birthYear: firstPerson?.personData.dateOfBirth.split('-')[0],
            gender: firstPerson?.personData.gender,
            currentMovedToCHType: preInsurer?.currentMovedToCHType || ANSWER.NONE,
          },
          location: {
            premiumRegion: basket.address?.primeRegion,
            plz: basket.address?.zipCode,
          },
          preInsurer: preInsurerAnswer,
          order: {
            countOffers: personsWithOffer.length,
            confirmationStatus: basket?.egate?.applicationState,
          },
          personsScreenSource,
        },
      }

      TrackerHelper.tracker.set(data)

      TrackerHelper.setDistributionPartners(basket.distributionPartners)

      // trigger queued rules
      if (queuedRulesAfterBasketUpdate.length > 0) {
        queuedRulesAfterBasketUpdate.forEach(tracking => {
          bus.$emit(tracking.ruleId, tracking.payload)
        })
        queuedRulesAfterBasketUpdate.length = 0
      }
    })

    bus.$on(EVENT_OVP_ENTERED, trackingData => {
      if (trackingData) {
        TrackerHelper.tracker.set({
          data: { ...trackingData },
        })
      }

      TrackerHelper.trackAction(RULES.OVP_ENTERED)
    })

    bus.$on(EVENT_TRACK_AFTER_BASKET_UPDATE, deferred => {
      queuedRulesAfterBasketUpdate.push(deferred)
    })

    bus.$on(EVENT_PRODUCT_DURATION_CHANGED, ({ categoryId, source, productId }) => {
      TrackerHelper.setTrackingSourceComponent(source)
      const product = `${categoryId};${productId}`
      const productAction = 'modify'
      TrackerHelper.tracker.set({
        data: { basket: { productAction, product } },
      })
      TrackerHelper.trackAction(RULES.OVP_DURATION_CHANGED)
    })

    bus.$on(EVENT_PRODUCT_FRANCHISE_CHANGED, source => {
      TrackerHelper.setTrackingSourceComponent(source)
      TrackerHelper.trackAction(RULES.OVP_FRANCHISE_CHANGED)
    })

    bus.$on(EVENT_PRODUCT_DETAIL_OPEN, ({ categoryId, source, productId }) => {
      if (!productId) {
        return
      }

      TrackerHelper.setTrackingSourceComponent(source)
      const product = `${categoryId};${productId}`
      const productAction = 'view'
      TrackerHelper.tracker.set({
        data: { basket: { productAction, product } },
      })
      TrackerHelper.trackAction(RULES.OVP_PRODUCT_VIEWED)
    })

    bus.$on(EVENT_PRODUCT_ADDED, ({ categoryId, source, productId }) => {
      TrackerHelper.setTrackingSourceComponent(source)
      const product = `${categoryId};${productId}`
      const productAction = 'add'
      TrackerHelper.tracker.set({
        data: { basket: { productAction, product } },
      })
      TrackerHelper.trackAction(RULES.OVP_PRODUCT_ADDED_TO_BASKET)
    })

    bus.$on(EVENT_PRODUCT_REMOVED, ({ categoryId, source, productId }) => {
      TrackerHelper.setTrackingSourceComponent(source)
      const product = `${categoryId};${productId}`
      const productAction = 'remove'
      TrackerHelper.tracker.set({
        data: { basket: { productAction, product } },
      })
      TrackerHelper.trackAction(RULES.OVP_PRODUCT_REMOVED_FROM_BASKET)
    })

    // transfer doctor from other person
    bus.$on(EVENT_TRANSFER_DOCTOR_FROM_OTHER_DISPLAYED, () => {
      TrackerHelper.trackAction(RULES.OVP_DOCTOR_TRANSFER_FROM_OTHER_DISPLAYED)
    })

    bus.$on(EVENT_TRANSFER_DOCTOR_FROM_OTHER_APPLIED, () => {
      TrackerHelper.trackAction(RULES.OVP_DOCTOR_TRANSFER_FROM_OTHER_APPLIED)
    })

    bus.$on(EVENT_BASKET_OPEN_SCREEN_SAVE_MODAL, ({ source }) => {
      TrackerHelper.setTrackingSourceComponent(source)

      TrackerHelper.trackAction(RULES.OVP_SAVE_VIEW_DISPLAYED)
    })

    bus.$on(EVENT_BASKET_SAVED, ({ basket, source, update }) => {
      TrackerHelper.setTrackingSourceComponent(source)

      if (basket.mobile) {
        TrackerHelper.tracker.set({
          data: {
            basket: {
              initToOffer: TrackerHelper.computeSecondsTillNow(basket.createDate),
            },
          },
        })
        TrackerHelper.trackAction(update ? RULES.OVP_BASKET_UPDATED : RULES.OVP_BASKET_SAVED)
      } else {
        TrackerHelper.trackAction(RULES.OVP_BASKET_SAVED_NO_EGATE)
      }
    })

    // Reentry
    bus.$on(EVENT_REENTRY_LOADED, ({ basketId }) => {
      TrackerHelper.tracker.set({ data: { basket: { id: basketId } } })
    })

    bus.$on(EVENT_CONTRACT_OWNER_CHANGED, contractOwnerType => {
      TrackerHelper.tracker.set({ data: { contractOwnerType } })
      TrackerHelper.trackAction(RULES.OVP_SPOC_CHANGED)
    })

    bus.$on(EVENT_CUSTOMER_PORTAL_CHANGED, value => {
      TrackerHelper.trackAction(value ? RULES.OVP_KUPO_SELECTED : RULES.OVP_KUPO_UNSELECTED)
    })

    bus.$on(EVENT_APPLICATION_SUBMITTED, ({ basket }) => {
      TrackerHelper.tracker.set({
        data: {
          basket: {
            initToSubmit: TrackerHelper.computeSecondsTillNow(basket.createDate),
          },
        },
      })

      TrackerHelper.trackAction(RULES.OVP_OFFER_SUBMITTED)
    })

    bus.$on(EVENT_SYNC_EXECUTED, () => {
      TrackerHelper.trackAction(RULES.OVP_SYNC_SESSION_FUNNELED)
    })

    // Cobrowse
    bus.$on(EVENT_COBROWSE_PIN_DIALOG_OPEN, () => {
      TrackerHelper.trackAction(RULES.OVP_COBROWSE_PIN_DIALOG_OPEN)
    })
    bus.$on(EVENT_COBROWSE_SESSION_STARTED, () => {
      TrackerHelper.trackAction(RULES.OVP_COBROWSE_SESSION_STARTED)
    })
    bus.$on(EVENT_COBROWSE_SESSION_ENDED, () => {
      TrackerHelper.trackAction(RULES.OVP_COBROWSE_SESSION_ENDED)
    })

    // FAB
    bus.$on(EVENT_FAB_OPENED, () => {
      TrackerHelper.trackAction(RULES.OVP_FAB_OPENED)
    })

    // PartnerWidget
    bus.$on(EVENT_PARTNER_WIDGET_DISPLAYED, trackingData => {
      TrackerHelper.tracker.set({
        data: { ...trackingData },
      })

      TrackerHelper.trackAction(RULES.OVP_PARTNER_WIDGET_DISPLAYED)
    })
    bus.$on(EVENT_PARTNER_WIDGET_PAGE_VIEWED, trackingData => {
      if (trackingData) {
        TrackerHelper.tracker.set({
          data: { ...trackingData },
        })
      }

      TrackerHelper.tracker.trigger(TrackerHelper.tracker.triggerType.VIEW, RULES.OVP_PAGE_VIEWED)
    })
    bus.$on(EVENT_PARTNER_WIDGET_GOTO_SANITAS, () => {
      TrackerHelper.tracker.trigger(TrackerHelper.tracker.triggerType.VIEW, RULES.OVP_PARTNER_WIDGET_GOTO_SANITAS)
    })

    bus.$once(EVENT_BASKET_DIFF_OPENED, () => {
      TrackerHelper.trackAction(RULES.OVP_BASKET_DIFF_OPENED)
    })
  },
}
